import React, { FC } from "react"
import { Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material"
import { TK } from "../../../store/translations/translationKeys"
import { AutocompleteTextField } from './AutocompleteTextField'
import { useGeneralSettings } from '../../../store/settings/hooks'
import { useTranslations } from '../../../store/translations/hooks'
import Typography from "../../Typography"
import { updateGeneralClientsTagsSettings } from "../../../store/settings/actions"
import { ChromePicker } from "react-color"
import { useDispatch } from "react-redux"

interface ClientTagsProps {
  values: string[]
  onChange?: (tags: string[]) => void
  disabled?: boolean
}

export const ClientTags: FC<ClientTagsProps> = ({values, onChange, disabled}) => {
  const t = useTranslations()
  const dispatch = useDispatch()
  const {clientTags} = useGeneralSettings()
  const [editing, setEditing] = React.useState(false)
  const [newColor, setNewColor] = React.useState('#777')
  const [editingTag, setEditingTag] = React.useState<string|undefined>(undefined)

  const onAddNewTag = (newTag: string) => {
    dispatch(updateGeneralClientsTagsSettings({...clientTags, [newTag]: {color: '#777'}}))
  }

  const handleClick = () => {
    if (!editingTag && onChange) {
      setEditing(true)
    }
  }

  const handleRightClick = (tag: string): React.MouseEventHandler<HTMLElement> => (e) => {
    e.preventDefault()
    setEditingTag(tag)
    setNewColor(clientTags[tag].color)
  }

  const handleTagEditFinish = () => {
    if (editingTag) {
      dispatch(updateGeneralClientsTagsSettings({...clientTags, [editingTag]: {color: newColor}}))
      setEditingTag(undefined)
    }
  }

  return editing
    ? <AutocompleteTextField
        id='client-tags'
        multiSelect
        disabled={disabled}
        options={Object.keys(clientTags ?? {})}
        values={values}
        onBlur={() => setEditing(false)}
        onValuesChanged={onChange}
        onAddNewOption={onAddNewTag}
      />
    : <div style={{display: 'flex', gap: 'var(--spacer-XXS)', margin: 'var(--spacer-XS) 0', alignItems: 'center', flexWrap: 'wrap'}}>
        {values.length
            ? <>
                {values.map(tag => <Chip
                    id={tag}
                    key={tag}
                    size="small"
                    label={tag}
                    style={{backgroundColor: clientTags[tag]?.color, cursor: onChange ? 'pointer' : 'default'}}
                    onContextMenu={handleRightClick(tag)}
                    onDelete={onChange && !disabled ? () => onChange(values.filter(v => v !== tag)) : undefined}
                />)}
                {onChange && !disabled && (
                    <Chip
                        size="small"
                        label="+"
                        variant="outlined"
                        onClick={handleClick}
                        style={{cursor: 'pointer', fontSize: '1rem', lineHeight: 1, color: 'var(--text-muted)', borderColor: 'var(--appLigthGrey)'}}
                    />
                )}
              </>
            : onChange && !disabled
                ? <span style={{color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85em', fontStyle: 'italic'}} onClick={handleClick}>{t(TK.addTag)}...</span>
                : null
        }
        <Dialog open={!!editingTag} onClose={() => setEditingTag(undefined)}>
          <DialogTitle>{editingTag}</DialogTitle>
          <DialogContent>
            <ChromePicker color={newColor} onChange={color => setNewColor(color.hex)} onChangeComplete={color => setNewColor(color.hex)}/>
            <div>
              {[...new Set(Object.values(clientTags ?? {}).map(({color}) => color))].map(color =>
                <Button key={color} style={{backgroundColor: color}} onClick={() => setNewColor(color)} />)
              }
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTagEditFinish}>{t(TK.save)}</Button>
          </DialogActions>
        </Dialog>
    </div>
}
